import { useI18n } from "@/i18n";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "sonner";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import {
  CalendarClock, Truck, FileText, Users, ClipboardList, Clock,
  Search, FilePlus, Briefcase, Zap, Compass, MapPin, Settings as SettingsIcon,
  ShoppingCart, Wrench, Building2, Receipt, Factory, Phone,
} from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator, CommandShortcut, Command,
} from "@/components/ui/command";
import { Command as CommandPrimitive } from "cmdk";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  getSearchIndex, getTechnicians, listDispatches,
  updateDispatchStatus, updateWOStatus,
} from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { getRecents, pushRecent } from "@/lib/recents";
import { setFocusTarget, FOCUS_PO, FOCUS_INVOICE, FOCUS_TECH, FOCUS_TECH_DISPATCH, FOCUS_VENDOR } from "@/lib/deepLinkFocus";

// Business-record search (customers, proposals, WOs…) unlocks when the
// permissions matrix grants any of these surfaces.
const BIZ_SURFACES = ["sales.customers", "sales.proposals", "sales.leads", "ops.work_orders", "ops.purchasing", "ops.invoices"];

// iter243 — the palette now does its OWN filtering (shouldFilter=false
// on cmdk). cmdk's default scorer requires query characters to appear
// in order, so multi-word queries like "denver acme" silently dropped
// valid matches — the "global search doesn't find things" bug. Token
// matching: every whitespace-separated token must appear somewhere in
// the item's haystack, in any order.
function tokenMatch(haystack, query) {
  const h = haystack.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((t) => h.includes(t));
}

// Static navigation map — each entry is gated by the permissions matrix
// surface in NAV_SURFACES (same table Settings → Users → Permissions edits).
const NAV_ITEMS = [
  { id: "nav-dashboard",    label: "Dashboard",      path: "/",            icon: Compass,       keywords: "home overview metrics" },
  { id: "nav-leads",        label: "Leads",          path: "/leads",       icon: Briefcase,     keywords: "prospects pipeline" },
  { id: "nav-proposals",    label: "Proposals",      path: "/proposals",   icon: ClipboardList, keywords: "estimates quotes" },
  { id: "nav-clients",      label: "Customers",      path: "/clients",     icon: Users,         keywords: "clients accounts" },
  { id: "nav-work-orders",  label: "Work Orders",    path: "/work-orders", icon: FileText,      keywords: "wo jobs service" },
  { id: "nav-dispatch",     label: "Dispatch",       path: "/dispatch",    icon: Truck,         keywords: "schedule board assign" },
  { id: "nav-timesheets",   label: "Timesheets",     path: "/timesheets",  icon: Clock,         keywords: "hours payroll" },
  { id: "nav-purchasing",   label: "Purchasing",     path: "/purchasing",  icon: ShoppingCart,  keywords: "po vendor procurement receiving warehouse" },
  { id: "nav-field",        label: "My Field Day",   path: "/field",       icon: Wrench,        keywords: "tech today route" },
  { id: "nav-map",          label: "Map",            path: "/map",         icon: MapPin,        keywords: "live techs" },
  { id: "nav-settings",     label: "Settings",       path: "/settings",    icon: SettingsIcon,  keywords: "admin config" },
];

const NAV_SURFACES = {
  "nav-dashboard":   ["ops.dashboard"],
  "nav-leads":       ["sales.leads"],
  "nav-proposals":   ["sales.proposals"],
  "nav-clients":     ["sales.customers"],
  "nav-work-orders": ["ops.work_orders"],
  "nav-dispatch":    ["ops.dispatch"],
  "nav-timesheets":  ["ops.timesheets"],
  "nav-purchasing":  ["ops.purchasing", "ops.warehouse"],
  "nav-field":       ["ops.tech"],
  "nav-map":         ["ops.map"],
  "nav-settings":    ["settings.general", "settings.users", "settings.dispatch", "settings.equipment", "settings.vendors", "settings.inspections", "settings.geocoding", "settings.wiki"],
};

function canSeeNav(item, user, can) {
  const surfaces = NAV_SURFACES[item.id] || [];
  return !!(user && surfaces.some(s => can(s, "read")));
}

const EMPTY_INDEX = {
  clients: [], sites: [], proposals: [], leads: [], work_orders: [],
  purchase_orders: [], invoices: [], technicians: [], vendors: [],
};

// Money is searchable both raw and rounded: "1234.56", "1235", "1,235".
const amountHaystack = (v) => {
  const n = Number(v || 0);
  if (!n) return "";
  return `${n} ${Math.round(n)} ${Math.round(n).toLocaleString()}`;
};

const PROPOSAL_STATUS_LABELS = {
  draft: "Draft", bid_submitted: "Bid Submitted", won: "Won", lost: "Lost", cancelled: "Cancelled",
};

// Shared engine: corpus, groups, quick actions. `render(listBody)` lets the
// modal (phone) and the inline header dropdown (desktop) share one body.
function PaletteBody({ open, onOpenChange, query, setQuery, render, resetOnOpen = true }) {
  const navigate = useNavigate();
  const { user, can, access } = useAuth();
  const canSeeBiz = !!(user && access && BIZ_SURFACES.some(s => can(s, "read")));

  const [recents, setRecents] = useState([]);
  const [idx, setIdx] = useState(EMPTY_INDEX);
  const [busy, setBusy] = useState(false);

  // Refresh recents + the global search corpus every time the palette
  // opens (slim payload — no scopes/blobs). Stale data stays visible
  // while the refresh is in flight.
  useEffect(() => {
    if (!open) return;
    if (resetOnOpen) setQuery("");
    setRecents(getRecents());
    getSearchIndex(canSeeBiz ? undefined : "people")
      .then((r) => setIdx({ ...EMPTY_INDEX, ...(r.data || {}) }))
      .catch((e) => {
        // Don't swallow silently — an RBAC/session failure here shows up
        // as an eerily empty palette otherwise.
        console.warn("[cmdk] search index unavailable:", e?.response?.status || e);
      });
  }, [open, canSeeBiz]);

  // Wrap navigate so every command also bumps the entry into recents.
  // Quick Actions (type: "action") are intentionally excluded — they
  // are one-shot and shouldn't pollute the Recent list.
  const go = useCallback((entry) => {
    if (entry.type !== "action") pushRecent(entry);
    // iter273 — PO / invoice rows deep-link into the parent WO; the target
    // id is stashed so the section can expand itself once it mounts.
    if (entry.type === "po") setFocusTarget(FOCUS_PO, entry.id);
    if (entry.type === "invoice") setFocusTarget(FOCUS_INVOICE, entry.id);
    // iter274 — people rows: the tech drawer / dispatch row / vendor card
    // expand themselves once the destination mounts.
    if (entry.type === "tech") setFocusTarget(FOCUS_TECH, entry.id);
    if (entry.type === "tech-dispatch") setFocusTarget(FOCUS_TECH_DISPATCH, entry.id);
    if (entry.type === "vendor") setFocusTarget(FOCUS_VENDOR, entry.id);
    onOpenChange(false);
    navigate(entry.path, entry.state ? { state: entry.state } : undefined);
  }, [navigate, onOpenChange]);

  // Quick Action: "Start today's first WO". Resolves the current
  // user → technician, fetches today's dispatches, picks the first
  // `scheduled` row (by start time), flips it to en_route at both
  // dispatch and WO level, then drops the user on /field so they can
  // see the change. All API failures degrade gracefully into a toast.
  const startTodaysFirstWO = useCallback(async () => {
    setBusy(true);
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const techsRes = await getTechnicians();
      const techs = (techsRes.data || []).filter((t) => !t.is_archived);
      let techId = null;
      if (user?.email) {
        const me = techs.find(
          (t) => (t.email || "").toLowerCase() === user.email.toLowerCase(),
        );
        if (me) techId = me.id;
      }
      if (!techId) techId = localStorage.getItem("my_tech_id") || techs[0]?.id;
      if (!techId) {
        toast.error("No technicians found to start a WO for.");
        return;
      }
      const dispRes = await listDispatches({
        tech_id: techId, start_date: today, end_date: today,
      });
      const dispatches = dispRes.data || [];
      const scheduled = dispatches
        .filter((d) => d.status === "scheduled")
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
      const first = scheduled[0];
      if (!first) {
        toast.info("No scheduled work orders for today.");
        onOpenChange(false);
        navigate("/field");
        return;
      }
      const dispatchPayload = { status: "en_route", reason: "Started via Cmd+K palette" };
      await updateDispatchStatus(first.id, dispatchPayload);
      try {
        await updateWOStatus({ wo_id: first.work_order_id, status: "en_route" });
      } catch {
        // WO-level mirror may already be en_route; not fatal.
      }
      toast.success(`Started ${first.wo_number || "today's first WO"}`);
      onOpenChange(false);
      navigate("/field");
    } catch {
      toast.error("Couldn't start today's first WO.");
    } finally {
      setBusy(false);
    }
  }, [user?.email, onOpenChange, navigate]);

  const openDispatchToday = useCallback(() => {
    onOpenChange(false);
    navigate("/dispatch", { state: { reset: Date.now() } });
  }, [navigate, onOpenChange]);

  // Quick Actions — gated by the permissions matrix.
  const quickActions = useMemo(() => {
    const acts = [];
    if (user && (can("ops.tech", "read") || can("ops.work_orders", "read"))) {
      acts.push({
        id: "act-start-first-wo",
        type: "action",
        label: "Start today's first WO",
        hint: "Sets status to En Route and opens it",
        icon: Zap,
        keywords: "start begin first wo work order today route",
        run: startTodaysFirstWO,
      });
    }
    if (user && can("ops.dispatch", "read")) {
      acts.push({
        id: "act-open-dispatch-today",
        type: "action",
        label: "Open Dispatch for today",
        hint: "Jumps to the dispatch board",
        icon: CalendarClock,
        keywords: "dispatch board schedule today",
        run: openDispatchToday,
      });
      acts.push({
        id: "act-new-client",
        type: "action",
        label: "New Customer",
        hint: "Creates and opens a fresh customer record",
        icon: Users,
        keywords: "new add create customer client account",
        run: () => go({ type: "action", id: "nav-new-client", path: "/clients", state: { commandNewClient: true } }),
      });
      acts.push({
        id: "act-new-estimate",
        type: "action",
        label: "New Estimate",
        hint: "Opens Proposals to build one",
        icon: FilePlus,
        keywords: "new add estimate proposal quote",
        run: () => { onOpenChange(false); navigate("/proposals"); },
      });
      acts.push({
        id: "act-search-po",
        type: "action",
        label: "Search by PO #",
        hint: "Opens Purchasing inbox for PO lookup",
        icon: Search,
        keywords: "po purchase order search find vendor",
        run: () => { onOpenChange(false); navigate("/purchasing"); },
      });
    }
    if (user && (can("ops.tech", "read") || can("ops.timesheets", "read"))) {
      acts.push({
        id: "act-clock-toggle",
        type: "action",
        label: "Clock In / Out",
        hint: "Opens your timesheet",
        icon: Clock,
        keywords: "clock in out time punch shift start stop",
        run: () => {
          onOpenChange(false);
          const target = can("ops.timesheets", "read") ? "/timesheets" : "/field";
          navigate(target);
        },
      });
    }
    return acts;
  }, [user, can, startTodaysFirstWO, openDispatchToday, go, navigate, onOpenChange]);

  // Field roles can't open Settings, so their tech/vendor row hands over
  // the number instead of navigating.
  const copyContact = useCallback((p) => {
    const line = [p.phone, p.email].filter(Boolean).join(" · ");
    if (!line) { toast.error(`No phone or email on file for ${p.name}`); return; }
    if (navigator.clipboard) navigator.clipboard.writeText(p.phone || p.email).catch(() => {});
    toast.success(`${p.name} — ${line}`, { description: "Copied to clipboard" });
    onOpenChange(false);
  }, [onOpenChange]);

  const q = query.trim();
  const isSearching = q.length > 0;

  const visibleActions = useMemo(() => {
    if (!isSearching) return quickActions;
    return quickActions.filter((a) => tokenMatch(`${a.label} ${a.keywords}`, q));
  }, [quickActions, q, isSearching]);

  const visibleNav = useMemo(() => {
    const items = NAV_ITEMS.filter((item) => canSeeNav(item, user, can));
    if (!isSearching) return items;
    return items.filter((item) => tokenMatch(`${item.label} ${item.keywords}`, q));
  }, [user, q, isSearching]);

  // Customers match on name, city/state, customer number, AND every
  // contact's name/email/phone.
  const visibleClients = useMemo(() => {
    if (!canSeeBiz) return [];
    if (!isSearching) return idx.clients.slice(0, 5);
    return idx.clients
      .filter((c) => tokenMatch(
        `${c.name || ""} ${c.city || ""} ${c.state || ""} ${c.customer_number || ""} ` +
        (c.contacts || []).map((ct) => `${ct.name || ""} ${ct.email || ""} ${ct.phone || ""}`).join(" "),
        q,
      ))
      .slice(0, 8);
  }, [idx.clients, q, isSearching, canSeeBiz]);

  const visibleSites = useMemo(() => {
    if (!canSeeBiz || !isSearching) return [];
    return idx.sites
      .filter((s) => tokenMatch(
        `${s.name || ""} ${s.address || ""} ${s.city || ""} ${s.state || ""} ${s.client_name || ""}`,
        q,
      ))
      .slice(0, 6);
  }, [idx.sites, q, isSearching, canSeeBiz]);

  const visibleProposals = useMemo(() => {
    if (!canSeeBiz) return [];
    if (!isSearching) return [];
    return idx.proposals
      .filter((p) => tokenMatch(
        `${p.name || ""} ${p.client_name || ""} ${p.prepared_by || ""} ${p.status || ""}`,
        q,
      ))
      .slice(0, 8);
  }, [idx.proposals, q, isSearching, canSeeBiz]);

  const visibleLeads = useMemo(() => {
    if (!canSeeBiz) return [];
    if (!isSearching) return [];
    return idx.leads
      .filter((l) => tokenMatch(
        `${l.company_name || ""} ${l.contact_name || ""} ${l.email || ""} ${l.phone || ""} ${l.city || ""} ${l.status || ""}`,
        q,
      ))
      .slice(0, 8);
  }, [idx.leads, q, isSearching, canSeeBiz]);

  const visibleWOs = useMemo(() => {
    if (!canSeeBiz) return [];
    if (!isSearching) return idx.work_orders.slice(0, 5);
    return idx.work_orders
      .filter((wo) => tokenMatch(
        `${wo.wo_number || ""} ${wo.title || ""} ${wo.client_name || ""} ${wo.status || ""}`,
        q,
      ))
      .slice(0, 10);
  }, [idx.work_orders, q, isSearching, canSeeBiz]);

  // iter273 — POs match on PO #, vendor, WO # and customer. Picking one
  // opens the parent WO with that PO expanded (stock POs have no WO, so
  // they land on Purchasing → Receiving instead).
  const visiblePOs = useMemo(() => {
    if (!canSeeBiz || !isSearching) return [];
    return idx.purchase_orders
      .filter((p) => tokenMatch(
        `${p.po_number || ""} ${p.vendor || ""} ${p.manufacturer_name || ""} ` +
        `${p.wo_number || ""} ${p.client_name || ""} ${p.status || ""} ${amountHaystack(p.total)}`,
        q,
      ))
      .slice(0, 8);
  }, [idx.purchase_orders, q, isSearching, canSeeBiz]);

  const visibleInvoices = useMemo(() => {
    if (!canSeeBiz || !isSearching) return [];
    return idx.invoices
      .filter((inv) => tokenMatch(
        `${inv.invoice_number || ""} ${inv.customer_name || ""} ${inv.customer_po_number || ""} ` +
        `${inv.wo_number || ""} ${inv.status || ""} ${amountHaystack(inv.total)}`,
        q,
      ))
      .slice(0, 8);
  }, [idx.invoices, q, isSearching, canSeeBiz]);

  // iter274 — People: techs match on name, phone, email, skills and city.
  // Available to every role (a tech looking up a teammate's number), so
  // these are deliberately NOT behind canSeeBiz.
  const visibleTechs = useMemo(() => {
    if (!isSearching) return [];
    return idx.technicians
      .filter((t) => tokenMatch(
        `${t.name || ""} ${t.phone || ""} ${t.email || ""} ` +
        `${(t.skills || []).join(" ")} ${t.city || ""} ${t.state || ""}`,
        q,
      ))
      .slice(0, 6);
  }, [idx.technicians, q, isSearching]);

  const visibleVendors = useMemo(() => {
    if (!isSearching) return [];
    return idx.vendors
      .filter((v) => tokenMatch(
        `${v.name || ""} ${v.vendor_no || ""} ${v.purchasing_contact_name || ""} ` +
        `${v.purchasing_contact_email || ""} ${v.purchasing_contact_phone || ""} ${v.address || ""}`,
        q,
      ))
      .slice(0, 6);
  }, [idx.vendors, q, isSearching]);

  const recentIcon = (type) => {
    if (type === "wo") return <FileText />;
    if (type === "client") return <Users />;
    if (type === "proposal") return <ClipboardList />;
    if (type === "lead") return <Briefcase />;
    if (type === "site") return <Building2 />;
    if (type === "po") return <ShoppingCart />;
    if (type === "invoice") return <Receipt />;
    return <Compass />;
  };

  const listBody = (
      <CommandList data-testid="command-palette-list">
        <CommandEmpty data-testid="command-palette-empty">
          No matches. Try a customer, contact, proposal, lead, WO #, PO #, invoice #, or page.
        </CommandEmpty>

        {visibleActions.length > 0 && (
          <CommandGroup heading="Quick Actions" data-testid="cmdk-group-actions">
            {visibleActions.map((a) => {
              const Icon = a.icon;
              return (
                <CommandItem
                  key={a.id}
                  value={a.id}
                  onSelect={() => !busy && a.run()}
                  data-testid={`cmdk-${a.id}`}
                >
                  <Icon className="text-violet-600" />
                  <span className="flex flex-col">
                    <span className="text-sm">{a.label}</span>
                    {a.hint && <span className="text-[11px] text-slate-500">{a.hint}</span>}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {!isSearching && recents.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Recent" data-testid="cmdk-group-recents">
              {recents.map((r) => (
                <CommandItem
                  key={`recent-${r.type}-${r.id}`}
                  value={`recent-${r.type}-${r.id}`}
                  onSelect={() => go(r)}
                  data-testid={`cmdk-recent-${r.id}`}
                >
                  {recentIcon(r.type)}
                  <span className="flex flex-col">
                    <span className="text-sm">{r.label}</span>
                    {r.hint && <span className="text-[11px] text-slate-500">{r.hint}</span>}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleNav.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Go to" data-testid="cmdk-group-nav">
              {visibleNav.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.id}
                    value={item.id}
                    onSelect={() => go({
                      type: "nav", id: item.id, label: item.label, path: item.path,
                    })}
                    data-testid={`cmdk-${item.id}`}
                  >
                    <Icon />
                    <span>{item.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </>
        )}

        {visibleClients.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Customers" data-testid="cmdk-group-clients">
              {visibleClients.map((c) => (
                <CommandItem
                  key={`client-${c.id}`}
                  value={`client-${c.id}`}
                  onSelect={() => go({
                    type: "client",
                    id: c.id,
                    label: c.name,
                    hint: c.city ? `${c.city}${c.state ? `, ${c.state}` : ""}` : undefined,
                    path: "/clients",
                    state: { openClientId: c.id },
                  })}
                  data-testid={`cmdk-client-${c.id}`}
                >
                  <Users />
                  <span className="flex flex-col">
                    <span className="text-sm">{c.name}</span>
                    {c.city && (
                      <span className="text-[11px] text-slate-500">
                        {c.city}{c.state ? `, ${c.state}` : ""}
                      </span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleSites.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Sites" data-testid="cmdk-group-sites">
              {visibleSites.map((s) => (
                <CommandItem
                  key={`site-${s.id}`}
                  value={`site-${s.id}`}
                  onSelect={() => go({
                    type: "site",
                    id: s.id,
                    label: s.name || s.address || "Site",
                    hint: s.client_name,
                    path: "/clients",
                    state: { openClientId: s.client_id },
                  })}
                  data-testid={`cmdk-site-${s.id}`}
                >
                  <Building2 />
                  <span className="flex flex-col">
                    <span className="text-sm">{s.name || s.address}</span>
                    <span className="text-[11px] text-slate-500">
                      {[s.client_name, s.city].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleProposals.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Proposals" data-testid="cmdk-group-proposals">
              {visibleProposals.map((p) => (
                <CommandItem
                  key={`proposal-${p.id}`}
                  value={`proposal-${p.id}`}
                  onSelect={() => go({
                    type: "proposal",
                    id: p.id,
                    label: p.name || "Untitled proposal",
                    hint: p.client_name,
                    path: "/proposals",
                    state: { viewProposalId: p.id },
                  })}
                  data-testid={`cmdk-proposal-${p.id}`}
                >
                  <ClipboardList />
                  <span className="flex flex-col">
                    <span className="text-sm">{p.name || "Untitled proposal"}</span>
                    <span className="text-[11px] text-slate-500">
                      {[p.client_name, p.value ? `$${Math.round(p.value).toLocaleString()}` : ""].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <CommandShortcut className="uppercase">{PROPOSAL_STATUS_LABELS[p.status] || p.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleLeads.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Leads" data-testid="cmdk-group-leads">
              {visibleLeads.map((l) => (
                <CommandItem
                  key={`lead-${l.id}`}
                  value={`lead-${l.id}`}
                  onSelect={() => go({
                    type: "lead",
                    id: l.id,
                    label: l.company_name || l.contact_name || "Lead",
                    hint: l.contact_name,
                    path: "/leads",
                    state: { openLeadId: l.id },
                  })}
                  data-testid={`cmdk-lead-${l.id}`}
                >
                  <Briefcase />
                  <span className="flex flex-col">
                    <span className="text-sm">{l.company_name || l.contact_name}</span>
                    <span className="text-[11px] text-slate-500">
                      {[l.contact_name, l.city].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <CommandShortcut className="uppercase">{l.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleWOs.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Work Orders" data-testid="cmdk-group-wos">
              {visibleWOs.map((wo) => (
                <CommandItem
                  key={`wo-${wo.id}`}
                  value={`wo-${wo.id}`}
                  onSelect={() => go({
                    type: "wo",
                    id: wo.id,
                    label: `${wo.wo_number} · ${wo.title || "Untitled"}`,
                    hint: wo.client_name,
                    path: "/clients",
                    state: { openClientId: wo.client_id, openTab: "workorders", openWorkOrderId: wo.id },
                  })}
                  data-testid={`cmdk-wo-${wo.id}`}
                >
                  <FileText />
                  <span className="flex flex-col">
                    <span className="text-sm">
                      <span className="font-mono text-blue-700">{wo.wo_number}</span>
                      {" · "}{wo.title || "Untitled"}
                    </span>
                    {wo.client_name && (
                      <span className="text-[11px] text-slate-500">{wo.client_name}</span>
                    )}
                  </span>
                  <CommandShortcut className="uppercase">{wo.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visiblePOs.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Purchase Orders" data-testid="cmdk-group-pos">
              {visiblePOs.map((p) => (
                <CommandItem
                  key={`po-${p.id}`}
                  value={`po-${p.id}`}
                  onSelect={() => go({
                    type: "po",
                    id: p.id,
                    label: p.po_number || "PO",
                    hint: [p.vendor || p.manufacturer_name, p.wo_number].filter(Boolean).join(" · "),
                    path: p.wo_id ? "/clients" : "/purchasing",
                    state: p.wo_id
                      ? { openClientId: p.client_id, openTab: "workorders", openWorkOrderId: p.wo_id }
                      : undefined,
                  })}
                  data-testid={`cmdk-po-${p.id}`}
                >
                  <ShoppingCart />
                  <span className="flex flex-col">
                    <span className="text-sm">
                      <span className="font-mono text-violet-700">{p.po_number}</span>
                      {" · "}{p.vendor || p.manufacturer_name || "—"}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {[p.wo_number, p.client_name, p.total ? `$${Math.round(p.total).toLocaleString()}` : ""]
                        .filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <CommandShortcut className="uppercase">{p.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleInvoices.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Invoices" data-testid="cmdk-group-invoices">
              {visibleInvoices.map((inv) => (
                <CommandItem
                  key={`invoice-${inv.id}`}
                  value={`invoice-${inv.id}`}
                  onSelect={() => go({
                    type: "invoice",
                    id: inv.id,
                    label: inv.invoice_number || "Invoice",
                    hint: [inv.customer_name, inv.wo_number].filter(Boolean).join(" · "),
                    path: "/clients",
                    state: {
                      openClientId: inv.client_id,
                      openTab: "workorders",
                      openWorkOrderId: inv.work_order_id,
                    },
                  })}
                  data-testid={`cmdk-invoice-${inv.id}`}
                >
                  <Receipt />
                  <span className="flex flex-col">
                    <span className="text-sm">
                      <span className="font-mono text-emerald-700">{inv.invoice_number}</span>
                      {" · "}{inv.customer_name || "—"}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {[inv.wo_number,
                        inv.customer_po_number ? `PO ${inv.customer_po_number}` : "",
                        inv.total ? `$${Math.round(inv.total).toLocaleString()}` : ""]
                        .filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <CommandShortcut className="uppercase">{inv.status}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {visibleTechs.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="People" data-testid="cmdk-group-techs">
              {visibleTechs.map((t) => [
                <CommandItem
                  key={`tech-${t.id}`}
                  value={`tech-${t.id}`}
                  onSelect={() => {
                    if (canSeeBiz) {
                      go({
                        type: "tech",
                        id: t.id,
                        label: t.name,
                        hint: t.phone || t.email || "Technician",
                        path: "/settings",
                        state: { tab: "users" },
                      });
                    } else {
                      copyContact(t);
                    }
                  }}
                  data-testid={`cmdk-tech-${t.id}`}
                >
                  <Wrench style={t.color ? { color: t.color } : undefined} />
                  <span className="flex flex-col">
                    <span className="text-sm">{t.name}</span>
                    <span className="text-[11px] text-slate-500">
                      {[t.phone, t.email, (t.skills || []).slice(0, 2).join(", ")]
                        .filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <CommandShortcut>{canSeeBiz ? "Profile" : "Copy #"}</CommandShortcut>
                </CommandItem>,
                canSeeBiz ? (
                  <CommandItem
                    key={`tech-disp-${t.id}`}
                    value={`tech-dispatch-${t.id}`}
                    onSelect={() => go({
                      type: "tech-dispatch",
                      id: t.id,
                      label: `Dispatch · ${t.name}`,
                      hint: "Jumps to their row on the board",
                      path: "/dispatch",
                    })}
                    data-testid={`cmdk-tech-dispatch-${t.id}`}
                  >
                    <Truck />
                    <span className="text-sm text-slate-600">Dispatch · {t.name}</span>
                    <CommandShortcut>Board</CommandShortcut>
                  </CommandItem>
                ) : null,
              ])}
            </CommandGroup>
          </>
        )}

        {visibleVendors.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Vendors" data-testid="cmdk-group-vendors">
              {visibleVendors.map((v) => (
                <CommandItem
                  key={`vendor-${v.id}`}
                  value={`vendor-${v.id}`}
                  onSelect={() => {
                    if (canSeeBiz) {
                      go({
                        type: "vendor",
                        id: v.id,
                        label: v.name,
                        hint: v.purchasing_contact_phone || v.purchasing_contact_email || "Vendor",
                        path: "/settings",
                        state: { tab: "manufacturers" },
                      });
                    } else {
                      copyContact({
                        name: v.name,
                        phone: v.purchasing_contact_phone,
                        email: v.purchasing_contact_email,
                      });
                    }
                  }}
                  data-testid={`cmdk-vendor-${v.id}`}
                >
                  <Factory />
                  <span className="flex flex-col">
                    <span className="text-sm">
                      {v.vendor_no && <span className="font-mono text-slate-400 mr-1.5">{v.vendor_no}</span>}
                      {v.name}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {[v.purchasing_contact_name, v.purchasing_contact_phone, v.purchasing_contact_email]
                        .filter(Boolean).join(" · ") || "No purchasing contact on file"}
                    </span>
                  </span>
                  <CommandShortcut>{canSeeBiz ? "Vendor" : "Copy #"}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
  );
  return render(listBody);
}
// Modal variant — used by the phone top bar (search icon) only.
export default function CommandPalette({ open, onOpenChange }) {
  const [query, setQuery] = useState("");
  return (
    <PaletteBody
      open={open}
      onOpenChange={onOpenChange}
      query={query}
      setQuery={setQuery}
      render={(listBody) => (
        <CommandDialog open={open} onOpenChange={onOpenChange} commandProps={{ shouldFilter: false }}>
          <VisuallyHidden.Root>
            <DialogTitle>Command Palette</DialogTitle>
            <DialogDescription>
              Search across navigation, customers, sites, proposals, leads, work orders, POs, invoices, and quick actions.
            </DialogDescription>
          </VisuallyHidden.Root>
          <CommandInput
            placeholder="Search customers, WOs, POs, invoices, people, vendors…"
            value={query}
            onValueChange={setQuery}
            data-testid="command-palette-input"
          />
          {listBody}
        </CommandDialog>
      )}
    />
  );
}

// Inline header variant — same corpus, same groups, same actions, but the
// results drop down under the header field instead of opening a modal.
// ⌘K focuses the field (Layout dispatches `global-search:focus`).
export function GlobalSearchInline() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  useEffect(() => {
    const focus = () => { inputRef.current?.focus(); setOpen(true); };
    window.addEventListener("global-search:focus", focus);
    return () => window.removeEventListener("global-search:focus", focus);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const close = useCallback(() => { setOpen(false); setQuery(""); inputRef.current?.blur(); }, []);
  return (
    <div ref={wrapRef} className="relative ml-auto mr-2 w-56 lg:w-64" data-testid="global-search">
      <PaletteBody resetOnOpen={false}
        open={open}
        onOpenChange={(v) => (v ? setOpen(true) : close())}
        query={query}
        setQuery={(q) => { setQuery(q); if (q) setOpen(true); }}
        render={(listBody) => (
          <Command shouldFilter={false} className="overflow-visible bg-transparent text-inherit" onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); close(); } }}>
            <div className={`flex h-7 items-center gap-2 rounded-md border px-2.5 text-sm transition-colors ${open ? "border-white/30 bg-white/[0.08]" : "border-white/10 bg-transparent hover:border-white/25"}`} cmdk-input-wrapper="">
              <CommandPrimitive.Input
                ref={inputRef}
                value={query}
                onValueChange={(q) => { setQuery(q); setOpen(true); }}
                onFocus={() => setOpen(true)}
                placeholder={t("Search")}
                className="h-7 w-full bg-transparent text-sm text-slate-100 outline-none placeholder:text-slate-500"
                data-testid="command-palette-input"
              />
              {!open && <kbd className="shrink-0 text-xs font-medium leading-none text-slate-500" data-testid="quick-search-kbd-icon">⌘K</kbd>}
            </div>
            {open && (
              <div className="absolute right-0 top-9 z-[60] w-[32rem] max-w-[90vw] overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-2xl [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2.5 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5"
                data-testid="global-search-dropdown">
                {listBody}
              </div>
            )}
          </Command>
        )}
      />
    </div>
  );
}
